import { Controller } from "@hotwired/stimulus"
import videojs from "video.js"

globalThis.feedMuted ??= true // start muted for mobile autoplay

// Tracks the single VideoJsController currently allowed to play,
// across the whole feed.
let currentlyPlayingController = null

// Keeps a pristine copy of each <video> element, keyed by the stable
// id rendered on its wrapper.
const pristineVideoCache = new Map()

export default class VideoJsController extends Controller {
  static targets = ["video"]

  connect() {
    this.videoElement = this.ensureVideoElement()

    if (!this.videoElement) return

    // ----------------------------------------------------
    // Manual Pause State
    // ----------------------------------------------------
    //
    // true  = user explicitly paused this video
    // false = video can be autoplayed when it becomes visible
    //
    // This state is deliberately separate from player.paused().
    // A video can be paused because it left the viewport without
    // being manually paused by the user.
    this.userPaused =
      this.element.dataset.userPaused === "true"

    // Used to distinguish a user clicking the native Video.js
    // play/pause button from an automatic pause().
    this.manualPauseRequested = false

    // Every play()/pause() call is chained onto this promise so
    // they run strictly one after another for this player.
    this.operationQueue = Promise.resolve()

    this.player = videojs(this.videoElement, {
      controls: true,

      controlBar: {
        children: [
          "playToggle",
          "progressControl",
          "volumePanel",
          "fullscreenToggle"
        ]
      },

      autoplay: false,
      preload: "none",
      loop: true,
      playsinline: true,
      muted: globalThis.feedMuted
    })

    this.setupFullscreenPortal()
    this.addCenterPlayButton()

    this.player.ready(() => {
      this.element.videoController = this

      this.player.muted(globalThis.feedMuted)

      // Tell the feed controller this player can now be played,
      // in case it became visible before Video.js finished initializing.
      this.element
        .closest("[data-controller~='feed-video']")
        ?.feedVideoController?.handleVideoReady()

      this.setupNativePlayToggleTracking()
    })

    // Global mute sync.
    this.player.on("volumechange", this.volumeChanged)

    // ----------------------------------------------------
    // Feed-wide single-video behavior
    // ----------------------------------------------------
    this.player.on("play", () => {
      if (
        currentlyPlayingController &&
        currentlyPlayingController !== this
      ) {
        // IMPORTANT:
        // This is NOT a user pause.
        // The previous video is being paused because another
        // video started playing.
        currentlyPlayingController.pause()
      }

      currentlyPlayingController = this
    })

    this.player.on("pause", () => {
      if (currentlyPlayingController === this) {
        currentlyPlayingController = null
      }

      // Only a pause explicitly initiated by the user should
      // set userPaused.
      if (this.manualPauseRequested) {
        this.userPaused = true
        this.element.dataset.userPaused = "true"

        this.manualPauseRequested = false
      }
    })

    document.addEventListener(
      "feed:mute-change",
      this.muteListener
    )

    // Turbo freezes a snapshot of the current page via turbo:before-cache.
    this.handleTurboBeforeCache =
      this.handleTurboBeforeCache.bind(this)

    document.addEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    )
  }

  disconnect() {
    document.removeEventListener(
      "feed:mute-change",
      this.muteListener
    )

    document.removeEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCache
    )

    this.removeNativePlayToggleTracking()

    if (currentlyPlayingController === this) {
      currentlyPlayingController = null
    }

    this.destroyPlayer()
  }

  handleTurboBeforeCache() {
    this.destroyPlayer()
    this.restorePristineVideoElement()
  }

  destroyPlayer() {
    this.element.videoController = null

    this.restoreFullscreenPlayer?.()

    if (this.player && !this.player.isDisposed()) {
      this.player.dispose()
    }

    this.player = null
    this.videoElement = null
  }

  // Video.js removes the native element when disposed.
  // Restore clean markup synchronously so Turbo stores a player
  // that can be initialized again.
  restorePristineVideoElement() {
    const storedClone = pristineVideoCache.get(
      this.cacheKey()
    )

    if (!storedClone) return

    this.element.replaceChildren(
      storedClone.cloneNode(true)
    )
  }

  // Makes sure there is a clean, unprocessed <video> element
  // to hand to Video.js.
  ensureVideoElement() {
    const key = this.cacheKey()

    const nativeVideo = Array.from(
      this.element.querySelectorAll(
        "video[data-video-js-target='video']"
      )
    ).find(video => this.isPristine(video))

    if (nativeVideo) {
      pristineVideoCache.set(
        key,
        nativeVideo.cloneNode(true)
      )
    }

    const storedClone = pristineVideoCache.get(key)

    if (!storedClone) return null

    const freshVideo = storedClone.cloneNode(true)

    this.element.replaceChildren(freshVideo)

    return freshVideo
  }

  isPristine(element) {
    return !element.classList.contains("vjs-tech")
  }

  cacheKey() {
    return this.element.id || this.element
  }

  // ----------------------------------------------------
  // User Pause / Play Tracking
  // ----------------------------------------------------

  /**
   * Track clicks on Video.js's native play/pause button.
   *
   * This runs in capture phase so we can determine whether the
   * user is trying to pause BEFORE Video.js performs its own
   * play/pause operation.
   */
  setupNativePlayToggleTracking() {
    if (!this.player || this.nativePlayToggleHandler) return

    const playToggle = this.player.controlBar?.playToggle

    if (!playToggle) return

    this.nativePlayToggleElement =
      playToggle.el()

    if (!this.nativePlayToggleElement) return

    this.nativePlayToggleHandler = () => {
      if (!this.player || this.player.isDisposed()) return

      // If currently playing, the user is about to pause.
      if (!this.player.paused()) {
        this.manualPauseRequested = true
      } else {
        // User is manually pressing Play.
        this.userPaused = false
        this.element.dataset.userPaused = "false"
      }
    }

    this.nativePlayToggleElement.addEventListener(
      "click",
      this.nativePlayToggleHandler,
      true
    )
  }

  removeNativePlayToggleTracking() {
    if (
      !this.nativePlayToggleElement ||
      !this.nativePlayToggleHandler
    ) {
      return
    }

    this.nativePlayToggleElement.removeEventListener(
      "click",
      this.nativePlayToggleHandler,
      true
    )

    this.nativePlayToggleElement = null
    this.nativePlayToggleHandler = null
  }

  /**
   * Clear the manual pause state.
   *
   * This is used when the user explicitly starts the video.
   */
  markUserPlaying() {
    this.userPaused = false
    this.element.dataset.userPaused = "false"
    this.manualPauseRequested = false
  }

  /**
   * Mark the video as manually paused.
   *
   * This is used by the custom center play/pause button.
   */
  markUserPaused() {
    this.userPaused = true
    this.element.dataset.userPaused = "true"
    this.manualPauseRequested = false
  }

  // ----------------------------------------------------
  // Global Mute
  // ----------------------------------------------------

  volumeChanged = () => {
    const muted = this.player.muted()

    if (globalThis.feedMuted === muted) return

    globalThis.feedMuted = muted

    document.dispatchEvent(
      new CustomEvent("feed:mute-change", {
        detail: { muted }
      })
    )
  }

  muteListener = event => {
    if (!this.player || this.player.isDisposed()) return

    this.player.muted(event.detail.muted)
  }

  // ----------------------------------------------------
  // Playback
  // ----------------------------------------------------

  isPlaying() {
    return (
      !!this.player &&
      !this.player.isDisposed() &&
      !this.player.paused()
    )
  }

  // Runs `task` only after any previously queued play()/pause()
  // call for this player has finished.
  enqueue(task) {
    this.operationQueue = this.operationQueue
      .then(task)
      .catch(() => { })

    return this.operationQueue
  }

  /**
   * Play the video.
   *
   * By default this is considered an automatic/system play.
   *
   * userInitiated: true is used when the actual user presses
   * the play button.
   */
  play({ userInitiated = false } = {}) {
    if (userInitiated) {
      this.markUserPlaying()
    }

    return this.enqueue(async () => {
      if (
        !this.player ||
        this.player.isDisposed()
      ) {
        return
      }

      if (this.isPlaying()) return

      try {
        this.player.muted(globalThis.feedMuted)

        await this.player.play()
      } catch (err) {
        // Browsers block autoplay for unmuted video without
        // a user gesture.
        if (!this.player.muted()) {
          try {
            this.player.muted(true)

            await this.player.play()
          } catch (err2) {
            // Still blocked - nothing more we can do without
            // a user gesture.
          }
        }
      }
    })
  }

  /**
   * Pause the video.
   *
   * IMPORTANT:
   *
   * userInitiated: false
   *   -> automatic pause, such as scrolling out of viewport.
   *
   * userInitiated: true
   *   -> user explicitly clicked pause.
   */
  pause({ userInitiated = false } = {}) {
    if (userInitiated) {
      this.markUserPaused()
    }

    return this.enqueue(async () => {
      if (
        !this.player ||
        this.player.isDisposed()
      ) {
        return
      }

      if (this.player.paused()) return

      this.player.pause()
    })
  }

  // ----------------------------------------------------
  // Fullscreen
  // ----------------------------------------------------

  setupFullscreenPortal() {
    const playerElement = this.player.el()
    const originalParent = playerElement.parentNode
    const originalNextSibling = playerElement.nextSibling
    const originalRequestFullscreen =
      this.player.requestFullscreen.bind(this.player)

    const fullscreenApi = this.player.fsApi_

    const feedController = this.element
      .closest("[data-controller~='feed-video']")
      ?.feedVideoController

    let placeholder = null
    let portal = null

    this.restoreFullscreenPlayer = () => {
      playerElement.classList.remove(
        "vjs-portal-active"
      )

      if (playerElement.parentNode !== originalParent) {
        if (
          originalNextSibling?.parentNode ===
          originalParent
        ) {
          originalParent.insertBefore(
            playerElement,
            originalNextSibling
          )
        } else {
          originalParent.appendChild(playerElement)
        }
      }

      placeholder?.remove()
      placeholder = null

      portal?.remove()
      portal = null

      feedController?.resumeSliderResponsiveUpdates()
    }

    const syncDocumentFullscreen = () => {
      if (
        !playerElement.classList.contains(
          "vjs-portal-active"
        )
      ) {
        return
      }

      const fullscreenElement =
        document[
        fullscreenApi.fullscreenElement
        ]

      const isFullscreen =
        fullscreenElement === portal ||
        fullscreenElement === playerElement

      this.player.isFullscreen(isFullscreen)

      this.player.controlBar?.fullscreenToggle
        ?.handleFullscreenChange()

      if (!isFullscreen) {
        this.restoreFullscreenPlayer()
      }
    }

    if (fullscreenApi?.fullscreenchange) {
      document.addEventListener(
        fullscreenApi.fullscreenchange,
        syncDocumentFullscreen
      )
    }

    this.player.requestFullscreen = (
      fullscreenOptions
    ) => {
      if (playerElement.parentNode === originalParent) {
        // Entering browser fullscreen changes window.innerWidth.
        // Freeze Slick's breakpoint refresh first.
        feedController?.suspendSliderResponsiveUpdates()

        const bounds =
          playerElement.getBoundingClientRect()

        placeholder =
          document.createElement("div")

        placeholder.className =
          "vjs-fullscreen-placeholder"

        placeholder.style.height =
          `${bounds.height}px`

        placeholder.setAttribute(
          "aria-hidden",
          "true"
        )

        originalParent.insertBefore(
          placeholder,
          playerElement
        )

        portal = document.createElement("div")

        portal.className =
          "vjs-fullscreen-portal"

        document.body.appendChild(portal)

        playerElement.classList.add(
          "vjs-portal-active"
        )

        portal.appendChild(playerElement)
      }

      try {
        const documentRequestFullscreen =
          fullscreenApi?.requestFullscreen &&
          portal?.[
          fullscreenApi.requestFullscreen
          ]

        const result =
          documentRequestFullscreen
            ? documentRequestFullscreen.call(
              portal,
              fullscreenApi.prefixed
                ? undefined
                : fullscreenOptions
            )
            : originalRequestFullscreen(
              fullscreenOptions
            )

        if (
          result &&
          typeof result.catch === "function"
        ) {
          result.catch(() =>
            this.restoreFullscreenPlayer()
          )
        }

        return result
      } catch (error) {
        this.restoreFullscreenPlayer()

        throw error
      }
    }

    this.player.on("dispose", () => {
      if (fullscreenApi?.fullscreenchange) {
        document.removeEventListener(
          fullscreenApi.fullscreenchange,
          syncDocumentFullscreen
        )
      }

      this.restoreFullscreenPlayer()
    })
  }

  // ----------------------------------------------------
  // Custom Center Play Button
  // ----------------------------------------------------

  addCenterPlayButton() {
    const wrapper = this.element.parentNode

    if (!wrapper) return

    // Guard against duplicate button.
    wrapper
      .querySelector(":scope > .center-play-btn")
      ?.remove()

    wrapper.classList.add(
      "custom-video-wrapper",
      "position-relative"
    )

    this.element.classList.add(
      "custom-controls-enabled"
    )

    const btn = document.createElement("div")

    btn.classList.add(
      "center-play-btn",
      "size-70",
      "position-absolute",
      "top-50",
      "start-50",
      "translate-middle",
      "d-flex",
      "align-items-center",
      "justify-content-center",
      "rounded-circle",
      "cursor-pointer"
    )

    btn.innerHTML = this.playIcon()

    btn.addEventListener("click", () => {
      if (this.player.paused()) {
        // User explicitly pressed Play.
        this.play({
          userInitiated: true
        })
      } else {
        // User explicitly pressed Pause.
        this.pause({
          userInitiated: true
        })
      }
    })

    wrapper.appendChild(btn)

    this.player.on("play", () => {
      btn.innerHTML = this.pauseIcon()
    })

    this.player.on("pause", () => {
      btn.innerHTML = this.playIcon()
    })
  }

  playIcon() {
    return `
      <svg
        class="pe-none"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="white"
      >
        <polygon points="8,5 19,12 8,19"></polygon>
      </svg>
    `
  }

  pauseIcon() {
    return `
      <svg
        class="pe-none"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="white"
      >
        <rect x="6" y="5" width="4" height="14"></rect>
        <rect x="14" y="5" width="4" height="14"></rect>
      </svg>
    `
  }
}
